import { IBasketItems } from 'src/pages/OrderEntryForm/OrderEntrySlice';
import {
  getObjectFromStorage,
  removeItemFromStorage,
  setObjectInStorage,
} from '../../../services/storageService';
import { v4 as uuidv4 } from 'uuid';

export const getSubstitutionHoldStorageKey = (swapId: string, basketVersion: number) =>
  `substitutionHoldIds_${swapId}_${basketVersion}`;

export const getSubstitutionHoldIds = (
  swapId: string | undefined,
  basketVersion: number | undefined,
): string[] => {
  if (!swapId || basketVersion == null) {
    return [];
  }
  return getObjectFromStorage(getSubstitutionHoldStorageKey(swapId, basketVersion)) || [];
};

export const appendSubstitutionHoldIds = (
  swapId: string | undefined,
  basketVersion: number | undefined,
  newIds: string[],
): string[] => {
  if (!swapId || basketVersion == null || !newIds?.length) {
    return getSubstitutionHoldIds(swapId, basketVersion);
  }

  const existingIds = getSubstitutionHoldIds(swapId, basketVersion);
  const activeHoldIds = [...new Set([...existingIds, ...newIds.filter(Boolean)])];
  setObjectInStorage(getSubstitutionHoldStorageKey(swapId, basketVersion), activeHoldIds);
  return activeHoldIds;
};

export const removeSubstitutionHoldId = (
  swapId: string | undefined,
  basketVersion: number | undefined,
  holdId: string,
) => {
  if (!swapId || basketVersion == null || !holdId) {
    return;
  }

  const updatedHoldIds = getSubstitutionHoldIds(swapId, basketVersion).filter(
    (id) => id !== holdId,
  );

  const storageKey = getSubstitutionHoldStorageKey(swapId, basketVersion);
  if (updatedHoldIds.length > 0) {
    setObjectInStorage(storageKey, updatedHoldIds);
  } else {
    removeItemFromStorage(storageKey);
  }
};

export const clearSubstitutionHoldIds = (
  swapId: string | undefined,
  basketVersion: number | undefined,
) => {
  if (!swapId || basketVersion == null) {
    return;
  }
  removeItemFromStorage(getSubstitutionHoldStorageKey(swapId, basketVersion));
};

export const belongsToCurrentDraftBasket = (
  item: { originalBasketId?: number | string | null } | undefined,
  currentBasketId: number | string | undefined,
): boolean =>
  item?.originalBasketId != null &&
  currentBasketId != null &&
  String(item.originalBasketId) === String(currentBasketId);

export const rehydrateSubstitutionHoldIdsFromBasket = (
  swapId: string | undefined,
  currentBasket: any,
): string[] => {
  const basketVersion = currentBasket?.version;
  if (!swapId || basketVersion == null || currentBasket?.status !== 'DRAFT_FOR_EDIT') {
    return getSubstitutionHoldIds(swapId, basketVersion);
  }

  const inferredHoldIds =
    currentBasket?.rollupItemDetails?.basketItems
      ?.filter((item) => {
        if (!item?.tradeId) {
          return false;
        }

        if (!belongsToCurrentDraftBasket(item, currentBasket?.id)) {
          return false;
        }

        return true;
      })
      ?.map((item) => item.tradeId)
      ?.filter(Boolean) || [];

  if (!inferredHoldIds.length) {
    return getSubstitutionHoldIds(swapId, basketVersion);
  }

  return appendSubstitutionHoldIds(swapId, basketVersion, inferredHoldIds);
};

export const isCurrentSubstitutionHold = (
  tradeId: string | undefined,
  swapId: string | undefined,
  basketVersion: number | undefined,
  holdIdsOverride?: string[],
  item?: { quantity?: number; substitutionQuantity?: number | null },
  currentBasketId?: number | string,
): boolean => {
  if (!tradeId) {
    return false;
  }

  const holdIds = holdIdsOverride ?? getSubstitutionHoldIds(swapId, basketVersion);
  if (!holdIds.includes(tradeId)) {
    return false;
  }

  if (item) {
    const qty = Number(item.quantity) || 0;
    const subQty = Number(item.substitutionQuantity) || 0;
    if (qty > 0 && subQty === 0 && !belongsToCurrentDraftBasket(item as any, currentBasketId)) {
      return false;
    }
  }

  return true;
};

export const enrichBasketItemsFromRolledItems = (
  // items: any[] = [],
  consolidatedItems: any[] = [],
): any[] => {
  return consolidatedItems.map((item) => {
    return {
      ...item,
      uniqueId: uuidv4(),
      tradeId: item.originalHoldId ?? null,
    };
  });
  // if (!items.length || !rolledItems.length) {
  //   return items;
  // }
  // return items.map((item) => {
  //   const isZeroed = (item.quantity ?? 0) === 0 && (item.substitutionQuantity ?? 0) === 0;
  //   if (!isZeroed) {
  //     return item;
  //   }

  //   const rolledMatch = rolledItems.find((rolled) => {
  //     const securityMatch =
  //       rolled.security === item.security ||
  //       (rolled.bbgTickerExch && rolled.bbgTickerExch === item.bbgTickerExch);
  //     if (!securityMatch) {
  //       return false;
  //     }
  //     if (item.originalBasketId != null && rolled.basketId != null) {
  //       return String(rolled.basketId) === String(item.originalBasketId);
  //     }
  //     return true;
  //   });

  //   if (!rolledMatch || (rolledMatch.quantity ?? 0) === 0) {
  //     return item;
  //   }

  //   return {
  //     ...item,
  //     quantity: rolledMatch.quantity ?? 0,
  //     substitutionQuantity: rolledMatch.substitutionQuantity ?? 0,
  //   };
  // });
};

export const addNewRowStatus = (items: any[], results: string[]) => {
  return items.map((item) => {
    if (results.includes(item.tradeId)) {
      return {
        ...item,
        isNewlyAdded: true,
      };
    }
    return item;
  });
};

export const getPreviousBasketsRowData = (basket) => {
  return (
    basket?.rollupItemDetails?.basketItems?.map((b: IBasketItems) => {
      return {
        fund: b.fundId,
        tradeDate: b.tradeDate,
        ticker: b.bbgTickerExch,
        securityName: b.securityName,
        hedgedPrice: b.newPrice ?? b.price,
        quantity: b.quantity,
        currency: b.inputCurrency,
        swapCurrency: b.swapCurrency,
        dividendRate: b.divRate,
        fxRate: b.fxRate,
        notional: b.notional,
        hedgedNotional: b.hedgedNotional,
        tradeType: b.tradeType,
        tradeAction: b.tradeAction,
        tradeSource: b.tradeSource,
        settlementDate: b.settlementDate,
      };
    }) || []
  );
};
